import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useHistory, useParams } from 'react-router-dom'

import { useSnackbar } from '../../../../components/stories/molecules/snackbar/component/snackbar-hook'
import { snackbarVariantOptions } from '../../../../components/stories/molecules/snackbar/constants'

import MoveItemsModal from '../../../../components/move-items-modal'
import { planMenuApiErrorCodeOptions } from '../../constants'
import { getOrganizationName } from '../../../../react-query/authentication/query/use-me-query'
import { useGetPlanFolderDetailsQuery } from '../../../../react-query/plan/query/use-get-plan-folder-details-query'
import { useMovePlansAndFoldersMutation } from '../../../../react-query/plan/mutation/use-move-plans-and-folders-mutation'
import { recursiveListTypeOptions } from '../../../../components/recursive-folder-list/constants'
import { getRootPlansWithFolders } from '../../../PlanSelection/actions'
import { useDispatch } from 'react-redux'
import { useGetProjectDetailsQuery } from '../../../../react-query/project/query/use-get-project-details-query'
import { usePlanMenuListViewControl } from '../../../../hooks/use-plan-menu-list-view-control'

const MoveFolderModal = () => {
  const history = useHistory()
  const { formatMessage } = useIntl()
  const { addSnackbar } = useSnackbar()

  const dispatch = useDispatch()

  const { itemId, projectId, destinationFolderId } = useParams()
  const [moveItemsError, setMoveItemsError] = useState(null)
  const [destinationFolderHint, setDestinationFolderHint] = useState(null)

  const companyName = getOrganizationName()
  const { viewType } = usePlanMenuListViewControl()

  const { data: projectDetails, isLoading: projectDetailsLoading } =
    useGetProjectDetailsQuery({
      projectId
    })

  const planRootFolderId = projectDetails?.planRootFolderId

  const getPlanTree = () => dispatch(getRootPlansWithFolders(projectId))

  const showSnackbarSuccess = () => {
    addSnackbar({
      message: formatMessage({ id: 'moveItems_folder_moved' }),
      snackbarType: snackbarVariantOptions.SUCCESS,
      hideCloseButton: true
    })
  }

  // Queries
  const { data: folderDetails, isLoading: folderDetailsLoading } =
    useGetPlanFolderDetailsQuery(itemId, {
      enabled: !!itemId
    })

  const { data: parentFolderDetails, isLoading: parentFolderDetailsLoading } =
    useGetPlanFolderDetailsQuery(folderDetails?.parentId, {
      enabled: !!folderDetails?.parentId
    })

  // Mutations
  const { mutate: moveItems, isPending: moveItemsLoading } =
    useMovePlansAndFoldersMutation({
      onSuccess: handleMoveProjectSuccess,
      onError: handleMoveProjectError
    })

  // handlers
  const handleOnClose = () => {
    history.push(`/project/${projectId}?view=${viewType}`)
  }

  function handleMoveProjectError(error) {
    // user doesn't have permission to move items
    if (
      error &&
      error?.response?.data?.status ===
        planMenuApiErrorCodeOptions.NO_PERMISSION_TO_CREATE_PLAN_OR_FOLDER
    ) {
      return setMoveItemsError(
        formatMessage({
          id: 'projectMenu_no_permission_to_move'
        })
      )
    }

    // folder with the same name already exists in the destination folder
    if (
      error &&
      error?.response?.data?.status ===
        planMenuApiErrorCodeOptions.FOLDER_NAME_IS_NOT_AVAILABLE
    ) {
      return setDestinationFolderHint(
        formatMessage({
          id: 'projectMenu_folder_with_this_name_aready_exists'
        })
      )
    }

    if (
      error &&
      error?.response?.data?.status ===
        planMenuApiErrorCodeOptions.FOLDER_NO_LOGER_EXISTS
    ) {
      return setDestinationFolderHint(
        formatMessage({
          id: 'recursiveFolderList_the_selected_folder_no_longer_exists'
        })
      )
    }

    return setMoveItemsError(
      formatMessage({
        id: 'moveItems_move_error'
      })
    )
  }

  function handleMoveProjectSuccess() {
    getPlanTree()
    handleOnClose()
    showSnackbarSuccess()
  }

  const handleMoveItems = ({ parentFolderId }) => {
    setDestinationFolderHint(null)
    setMoveItemsError(null)
    moveItems({
      folderIds: [itemId],
      destinationFolderId: parentFolderId,
      projectId
    })
  }

  const isLoading =
    folderDetailsLoading || parentFolderDetailsLoading || projectDetailsLoading
  const modalTitle = formatMessage({ id: 'projects_moveFolder' })

  const getOldFolderName = () => {
    if (
      parentFolderDetails?.name === companyName ||
      !parentFolderDetails?.name
    ) {
      return formatMessage({ id: 'main_folder' })
    }

    return parentFolderDetails?.name
  }

  const oldFolderName = getOldFolderName()

  // used fordrag and drop move
  useEffect(() => {
    if (destinationFolderId) {
      handleMoveItems({ parentFolderId: destinationFolderId })
    }
  }, [destinationFolderId])

  return (
    <MoveItemsModal
      handleClose={handleOnClose}
      handleMove={handleMoveItems}
      isLoading={isLoading}
      onMoveLoading={moveItemsLoading}
      onMoveError={moveItemsError}
      destinationFolderHint={destinationFolderHint}
      modalTitle={modalTitle}
      entityName={folderDetails?.name}
      oldFolderName={oldFolderName}
      isMultipleItems={false}
      planRootFolderId={planRootFolderId}
      foldersListType={recursiveListTypeOptions.PLAN}
      destinationFolderId={destinationFolderId}
      excludedFolderIds={[itemId]}
    />
  )
}

export default MoveFolderModal
